import * as Sentry from "@sentry/react";
import type { ErrorEvent, EventHint } from "@sentry/react";
import { router } from "./router";
import { isExpectedError } from "@/lib/expected-error";
import { initConnectivityTag } from "@/offline/sentry-connectivity";

/**
 * `beforeSend` filter — drops events that represent an *expected* failure the
 * dashboard should never show, and only those. See the "Offline and outbox
 * errors" variant in `specs/conventions/error-reporting.md`.
 *
 * `beforeSend` is the single policy gate: capture sites report liberally and
 * this filter decides what is expected. Everything else is left untouched — an
 * `ApiError` (any HTTP status, including a transient 5xx) is a genuine problem
 * and must still reach Sentry. Transient outbox retries are never captured at
 * the source, so they never arrive here.
 *
 * Detection prefers the live exception from `hint.originalException`, matched
 * by the shared `isExpectedError` predicate. A serialised-type fallback also
 * catches an `OfflineError` whose live instance is unavailable (cross-context
 * capture): it sets `this.name`, so the type survives serialisation.
 * `LoginRequiredError` sets no `name`, so its serialised type is the minified
 * class name in production — only the `instanceof` check is reliable. That is
 * enough: `beforeSend` runs at capture time, before the offline transport
 * queues anything, so a serialised-only event never reaches this filter.
 *
 * A `BoundaryCrashError` — the wrapper `QueryErrorBoundary` reports when an
 * expected error crashes the UI — is a distinct type and carries no
 * `OfflineError` exception value, so neither check drops it. Deliberate: a
 * crash is never an expected failure.
 */
export function dropExpectedEvents(
  event: ErrorEvent,
  hint: EventHint,
): ErrorEvent | null {
  if (isExpectedError(hint?.originalException)) return null;
  if (event.exception?.values?.[0]?.type === "OfflineError") return null;
  return event;
}

/**
 * Options for the browser offline transport. `flushAtStartup` and
 * `maxQueueSize` are valid runtime options of `makeBrowserOfflineTransport`
 * (consumed by the offline wrapper), but Sentry types `init`'s
 * `transportOptions` as `Partial<BrowserTransportOptions>` — the *base* fetch
 * options, which omit both. Neither the offline option interface nor its base
 * is re-exported from `@sentry/react`, so we describe just the two fields we
 * set here and cast at the `init` boundary (see below). The values stay fully
 * type-checked; only the assignment to the under-typed `transportOptions`
 * slot is narrowed.
 */
interface OfflineTransportOptions {
  /** Replay the IndexedDB queue shortly after app start, not just on reconnect. */
  flushAtStartup: boolean;
  /** Max events held in the IndexedDB queue (SDK default is 30). */
  maxQueueSize: number;
}

const offlineTransportOptions: OfflineTransportOptions = {
  flushAtStartup: true,
  maxQueueSize: 50,
};

const dsn = import.meta.env.VITE_SENTRY_DSN;

if (dsn) {
  Sentry.init({
    dsn,
    // `prod`/`dev` from Vercel's VERCEL_ENV (injected via vite.config), matching
    // the backend's Sentry environment so errors and traces filter across both
    // stacks. Falls back to MODE on local builds (no DSN there anyway).
    environment: __SENTRY_ENV__ ?? import.meta.env.MODE,
    sendDefaultPii: true,
    // Offline delivery: failed sends are queued in IndexedDB and replayed on
    // reconnect — `flushAtStartup` also replays them shortly after the next
    // app start, so an error captured in a dead zone still arrives even if
    // the app was closed in between. Non-negotiable per the error-reporting
    // convention. Default queue size is 30; 50 gives more headroom.
    transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
    // Cast: see `OfflineTransportOptions` above — Sentry under-types this slot.
    transportOptions:
      offlineTransportOptions as Parameters<
        typeof Sentry.init
      >[0]["transportOptions"],
    integrations: [
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 1.0,
    // Same-origin plus BOTH backend hosts — `otus-backend.onrender.com` (dev)
    // and `otus-backend-1.onrender.com` (prod) — so the browser attaches
    // `sentry-trace`/`baggage` headers in every environment and FE→BE traces
    // connect end to end. Matching only the dev host left prod traces broken.
    tracePropagationTargets: [
      /^\//,
      /^https:\/\/otus-backend(-1)?\.onrender\.com/,
    ],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Drop expected offline / auth-not-ready failures only — see `dropExpectedEvents`.
    beforeSend: dropExpectedEvents,
    // Proven noise filters carried over from the sibling otus-web app.
    ignoreErrors: [
      "Failed to fetch",
      "NetworkError when attempting to fetch resource",
      "Load failed",
      "AbortError",
      "ResizeObserver loop",
      "Non-Error promise rejection captured",
    ],
  });

  // Tag every event with the device's connectivity state at capture time.
  initConnectivityTag();
}
